/**
 * FrameImage — a captured-frame `<img>` that falls back to a placeholder once
 * its source fails to load. `MomentBlock`'s in-bubble thumbnail uses it so a
 * frame degrades to a labelled placeholder instead of a broken-image glyph;
 * the file viewer shows its own file card instead.
 */
import { useState } from 'react';

import { cn } from '../../../lib/utils';

export const FRAME_EXPIRED_LABEL = 'Frame unavailable';

/** `min-h` keeps the placeholder from collapsing to just the label's line-height
 *  when the caller's own className carries no intrinsic height (the callers
 *  size themselves off the `<img>`'s natural dimensions). */
const PLACEHOLDER_CLASS = 'flex min-h-[80px] items-center justify-center text-center text-[11px] text-muted-foreground';

export interface FrameUnavailableProps {
  alt: string;
  className?: string;
}

/** Stands in for a frame whose bytes couldn't be had, still named for a screen reader. */
export function FrameUnavailable({ alt, className }: FrameUnavailableProps) {
  return (
    <div className={cn(className, PLACEHOLDER_CLASS)} role="img" aria-label={alt}>
      {FRAME_EXPIRED_LABEL}
    </div>
  );
}

export interface FrameImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function FrameImage({ src, alt, className }: FrameImageProps) {
  const [expired, setExpired] = useState(false);

  function handleError(): void {
    setExpired(true);
  }

  if (expired) return <FrameUnavailable alt={alt} className={className} />;
  return <img src={src} alt={alt} className={className} onError={handleError} />;
}
